import React, { FC, Suspense, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useForm, SubmitHandler, FormProvider, Controller } from 'react-hook-form';
import Blade, { BladeContainer, BladeBody, BladeFooter } from '../Blade';
import { Kfh_BranchType, BranchContactInput } from '../../types/graphqlTypes';
import { Typography } from '../Typography';
import { Spacer } from '../Spacer';
import { Input } from '../forms/Input';
import { useCreate_Branch_ContactMutation } from '../../types/graphqlHelpers';
import { Panel } from '../Panel';
import { Select } from '../forms/Select';
import { TITLES, ABOUT_ME_SALES, ABOUT_ME_LETTINGS } from '../../const';
import { Textarea } from '../forms/Textarea';
import { TickCircle } from '../icon/icons/TickCircle';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { SmallPrint } from '../forms/SmallPrint';
import { Loader } from '../Loader';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { RecaptchaAction } from '../forms/RecaptchaAction';
import { Button } from '../Button';
import { alertBadEmail, validateEmail } from '../../utils/emailBlackList';

const PhoneNumberInput = React.lazy(() => import('../forms/PhoneNumberInput'));

interface BranchContactBladeProps {
  branch: Kfh_BranchType;
  enquiryType: 'Sales' | 'Lettings';
}

const StyledTitleWrap = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${space.md};
  `}
`;

const StyledLead = styled(Typography)`
  ${({ theme: { space } }) => css`
    max-width: 530px;
  `}
`;

export const BranchContactBlade: FC<BranchContactBladeProps> = ({ branch, enquiryType }) => {
  const { businessProfile } = branch;
  const salesContactDetails = businessProfile?.salesContactDetails;
  const lettingsContactDetails = businessProfile?.lettingsContactDetails;
  const { dataLayerPush } = useDataLayerPush();
  const [hasSent, setHasSent] = useState(false);

  // Mutation to submit the form
  const [createBranchContactMutation, { data, loading, error }] = useCreate_Branch_ContactMutation();

  const formMethods = useForm({
    mode: 'onSubmit',
  });

  const { register, handleSubmit, control, formState } = formMethods;
  const { errors, dirtyFields, touchedFields, isSubmitted } = formState;
  const { handleReCaptchaVerify, isHuman } = useRecaptcha({ formMethods, action: 'branchContact' });

  useEffect(() => {
    function checkSuccess() {
      if (!loading && data?.lead?.createBranchContact?.success) {
        setHasSent(true);
      }
    }
    checkSuccess();
  }, [data, dataLayerPush, loading]);

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit: SubmitHandler<BranchContactInput> = async (formData) => {
    alertBadEmail(formData.email);
    delete formData.captchaToken;
    const submitData = {
      ...formData,
      businessId: businessProfile.businessId,
      businessProfileName: businessProfile.businessName,
      isLettingsEnquiry: enquiryType === 'Lettings',
    };

    dataLayerPush({
      event: 'formSubmit',
      formName: 'branchContact',
      formValidation: 'success',
      formError: undefined,
      propertyMarket: enquiryType,
    });

    await createBranchContactMutation({
      variables: { branchContact: submitData },
    });
  };

  const handleReset = () => {
    formMethods.reset();
    setHasSent(false);
  };

  useEffect(() => {
    dataLayerPush({
      event: 'blade action',
      bladeEvent: 'opened',
      bladeOpen: true,
      bladeEventID: `branchContact`,
      clickBladeId: `branchContact`,
      bladeBranchName: `${branch.name}: ${enquiryType}`,
      bladePropertyDetails: undefined,
    });
  }, []);

  return (
    <Blade id="branchContact" customBlade>
      <FormProvider {...formMethods}>
        {(!isSubmitted || !hasSent) && (
          <BladeContainer as="form" onSubmit={handleSubmit(onSubmit)} method="post" noValidate>
            <BladeBody>
              <Spacer>
                <Spacer spacing="half">
                  <Typography variant="displaySmall">{enquiryType} Enquiry</Typography>
                  {enquiryType === 'Lettings' ? (
                    <p>
                      {lettingsContactDetails.telephone}
                      <br />
                      <a href={`mailto:${lettingsContactDetails.email}`}>{lettingsContactDetails.email}</a>
                    </p>
                  ) : (
                    <p>
                      {salesContactDetails.telephone}
                      <br />
                      <a href={`mailto:${salesContactDetails.email}`}>{salesContactDetails.email}</a>
                    </p>
                  )}
                </Spacer>

                <Panel as="fieldset" border>
                  <Spacer spacing="half">
                    <Typography component="legend" variant="titleMedium">
                      Send us a message
                    </Typography>
                    <Typography variant="smallprint">*Please complete all required form fields</Typography>
                    <Select
                      id="title"
                      label="Title"
                      {...register('title', { required: true })}
                      required
                      options={TITLES}
                      constrained
                      invalid={!!errors.title}
                      valid={touchedFields.title && dirtyFields.title && !errors.title}
                    />
                    <Input
                      type="text"
                      invalid={!!errors.forename}
                      valid={touchedFields.forename && dirtyFields.forename && !errors.forename}
                      label="First Name"
                      id="forename"
                      placeholder="e.g Sarah"
                      {...register('forename', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      label="Last Name"
                      id="surname"
                      invalid={!!errors.surname}
                      placeholder="e.g Smith"
                      valid={touchedFields.surname && dirtyFields.surname && !errors.surname}
                      {...register('surname', { required: true })}
                      required
                    />
                    <Input
                      type="email"
                      label="Email"
                      id="email"
                      invalid={!!errors.email}
                      valid={touchedFields.email && dirtyFields.email && !errors.email}
                      required
                      placeholder="e.g sarah@email.com"
                      {...register('email', {
                        required: true,
                        validate: validateEmail, // email spam check
                        pattern:
                          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      })}
                    />
                    <Controller
                      name="contactNumber"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Suspense fallback={<Loader size="small" />}>
                          <PhoneNumberInput
                            label="Phone"
                            id="PhoneNumber"
                            invalid={!!errors.contactNumber}
                            placeholder="Mobile or landline"
                            required
                            valid={touchedFields.contactNumber && dirtyFields.contactNumber && !errors.contactNumber}
                            {...field}
                          />
                        </Suspense>
                      )}
                    />
                    <Select
                      id="aboutMe"
                      label="About me"
                      {...register('aboutMe', { required: true })}
                      required
                      options={enquiryType === 'Sales' ? ABOUT_ME_SALES : ABOUT_ME_LETTINGS}
                      invalid={!!errors.aboutMe}
                      valid={touchedFields.aboutMe && dirtyFields.aboutMe && !errors.aboutMe}
                    />
                    <Textarea
                      invalid={!!errors.comments}
                      valid={touchedFields.comments && dirtyFields.comments && !errors.comments}
                      label="Your queries or comments"
                      id="comments"
                      {...register('comments', { required: true })}
                      required
                    />
                  </Spacer>
                </Panel>
                <RecaptchaAction show={!isHuman} handleReCaptchaVerify={handleReCaptchaVerify} />
                <SmallPrint />
              </Spacer>
            </BladeBody>
            <BladeFooter
              primaryButton={{
                label: 'Submit request',
                onClick: handleSubmit(onSubmit),
                disableClickTracking: true,
              }}
            />
          </BladeContainer>
        )}

        {isSubmitted && hasSent && (
          <BladeContainer>
            <BladeBody>
              <Panel>
                <Spacer>
                  <StyledTitleWrap>
                    <TickCircle size="extraLarge" />
                    <Typography variant="displaySmall">Received</Typography>
                  </StyledTitleWrap>
                  <StyledLead variant="lead">One of our team will be in contact as soon as possible.</StyledLead>
                  <Button variant="solid" onClick={handleReset}>
                    Send another message
                  </Button>
                </Spacer>
              </Panel>
            </BladeBody>
            <BladeFooter />
          </BladeContainer>
        )}
      </FormProvider>
    </Blade>
  );
};

export default BranchContactBlade;
